import { track } from "@amplitude/analytics-react-native";
import { useMemo } from "react";
import { View } from "react-native";
import { amp } from "src/api/amplitude";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import { StickerPackFragmentFragment } from "src/api/graphql/__generated__/graphql";
import { useStickerRowItemAllocation } from "src/components/StickerRow";
import parseStickerTemplateUrl from "src/helpers/parseV0TemplateUrl/parseStickerTemplateUrl";
import useDevice from "src/hooks/useDevice";
import useDim from "src/hooks/useDim";
import useGatheringHomePhotoSpacing from "src/screens/(internal)/(GatheringHome)/GatheringHome/hooks/useGatheringHomePhotoSpacing";
import { paddingLarge } from "src/styles/spacing";
import { useGatheringPreviewPublicStore } from "../../hooks/useGatheringPreviewPublicStore";
import useGatheringPublicPreviewInfo from "../../hooks/useGatheringPublicPreviewInfo";
import GatheringPreviewPublicPhotoItem, {
	GatheringPreviewPublicPhotoItemPhoto,
} from "./components/GatheringPreviewPublicPhotoItem";

const GatheringPreviewPublicPhotos = ({
	stickerPack,
}: {
	stickerPack: StickerPackFragmentFragment | null | undefined;
}) => {
	const gatheringId = useGatheringPreviewPublicStore((state) => state.gatheringId);
	const { gathering } = useGatheringPublicPreviewInfo(gatheringId);

	const dim = useDim();
	const numColumns = useMemo(() => {
		return Math.min(Math.max(Math.floor(dim.width / 250), 2), 6);
	}, [dim.width]);
	const { device } = useDevice();

	const rows = useMemo(() => {
		const rows: {
			photos: GatheringPreviewPublicPhotoItemPhoto[];
			blurTier: number;
			key: string;
		}[] = [];
		// n number of rows based on the size, and 3 blur rows
		const numBlurRows = 3;
		const numRows = (device === "desktop" ? 1 : 3) + numBlurRows;
		const blurStart = numRows - numBlurRows;
		for (const photo of gathering?.previewPhotos?.payload ?? []) {
			const endRow = rows[rows.length - 1];
			if (endRow && endRow.photos.length < numColumns) {
				endRow.photos.push(photo);
				endRow.key = `${endRow.key}|${photo.photoId}`;
			} else {
				rows.push({
					blurTier: Math.max(0, rows.length - blurStart + 1),
					photos: [photo],
					key: photo.photoId,
				});
			}
		}
		return rows.slice(0, numRows);
	}, [device, gathering?.previewPhotos?.payload, numColumns]);
	const { containerHorizontalPadding, spacing } = useGatheringHomePhotoSpacing();
	const width = useMemo(() => {
		return dim.width - 2 * containerHorizontalPadding + spacing;
	}, [containerHorizontalPadding, dim.width, spacing]);
	const setPopUpVariant = useGatheringPreviewPublicStore((state) => state.setPopUpVariant);
	const { getParsedSticker } = useStickerRowItemAllocation({
		numColumns,
		padding: spacing,
		stickers: (stickerPack?.stickers ?? []).map((sticker) => ({
			url: parseStickerTemplateUrl({ readUrl: sticker.readUrl }),
		})),
	});

	return (
		<View
			style={{
				alignItems: "center",
				marginTop: paddingLarge,
				overflow: "hidden",
			}}
		>
			{rows.map((row, rowIndex) => (
				<View
					key={row.key}
					style={{
						flexDirection: "row",
						width,
					}}
				>
					{row.photos.map((photo, columnIndex) => (
						<GatheringPreviewPublicPhotoItem
							key={photo.photoId}
							photo={photo}
							blurTier={row.blurTier}
							width={width / numColumns - spacing}
							containerStyle={{
								margin: spacing / 2,
							}}
							onPress={() => {
								setPopUpVariant(
									photo.variant === PhotoVariant.Video
										? "VideoDetail"
										: "PhotoDetail",
								);
								track(amp.gathering.previewSeeViewersPressed, {
									gatheringId: gathering?.gatheringId,
								});
							}}
							sticker={getParsedSticker({
								columnIndex: columnIndex,
								rowIndex: rowIndex,
							})}
						/>
					))}
				</View>
			))}
		</View>
	);
};
export default GatheringPreviewPublicPhotos;
